/**
 * Quick add behaviour for snippets/dorahi-product-card.liquid.
 * Port of dorahi-main/components/cart/quick-add-button.tsx.
 *
 * Adds the product's first variant via /cart/add, then dispatches the same
 * CartLinesUpdateEvent Horizon's product-form.js uses, so the cart bubble,
 * cart drawer re-render, and drawer auto-open all keep working.
 *
 * Loaded as a module from the snippet; the module map dedupes repeat loads
 * and the window guard dedupes the listener.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const STATES = ['plus', 'spinner', 'check'];

/**
 * @param {HTMLElement} button
 * @param {'plus' | 'spinner' | 'check'} state
 */
function setState(button, state) {
  for (const name of STATES) {
    const icon = button.querySelector(`[data-quick-add-${name}]`);
    if (icon) icon.classList.toggle('hidden', name !== state);
  }

  const face = button.querySelector('[data-quick-add-face]');
  if (!face) return;

  // Mirrors the React classes: pending -> opacity, success -> green.
  face.classList.toggle('opacity-50', state === 'spinner');
  face.classList.toggle('cursor-not-allowed', state === 'spinner');
  face.classList.toggle('text-green-600', state === 'check');
  face.classList.toggle('bg-green-50', state === 'check');
  face.classList.toggle('text-dorahi-pink', state !== 'check');
  face.classList.toggle('bg-white/90', state !== 'check');
  face.classList.toggle('hover:text-white', state === 'plus');
  face.classList.toggle('hover:bg-dorahi-pink', state === 'plus');
}

/** @param {HTMLElement} button */
async function quickAdd(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.dataset.busy) return;

  button.dataset.busy = 'true';
  setState(button, 'spinner');

  const routes = window.Theme?.routes ?? {};
  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(routes.cart_add_url || '/cart/add', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const result = await response.json();

    const ajaxCart = await fetch(`${routes.cart_url || '/cart'}.json`).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'dorahi-product-card',
        itemCount: 1,
        didError: Boolean(result.status),
      },
    });

    if (result.status) {
      // /cart/add error payload (sold out, limits, ...) — make it visible.
      console.warn('[dorahi-product-card] add to cart failed:', result.message || result.description);
      setState(button, 'plus');
      return;
    }

    // quick-add-button.tsx opened the cart drawer after adding.
    document.dispatchEvent(new CustomEvent('dorahi:cart:added'));

    setState(button, 'check');
    setTimeout(() => setState(button, 'plus'), 2000);
  } catch (error) {
    deferred.reject(error);
    console.warn('[dorahi-product-card] add to cart failed:', error);
    setState(button, 'plus');
  } finally {
    delete button.dataset.busy;
  }
}

if (!window.__dorahiQuickAddBound) {
  window.__dorahiQuickAddBound = true;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-quick-add]') : null;
    if (!(target instanceof HTMLElement)) return;

    // The button sits inside the product card <a>.
    event.preventDefault();
    event.stopPropagation();
    quickAdd(target);
  });
}
